import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, Wand2, AlertTriangle, CheckCircle2, Award, ExternalLink, Activity,
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Master "Recovery" page — bulk-fix Stripe payments that paid but never
 * promoted (silent webhook misses). Also surfaces recent webhook events so
 * the operator can see at a glance whether Stripe is delivering.
 */
export default function MasterRecovery() {
  const [purchases, setPurchases] = useState([]);
  const [needsRecovery, setNeedsRecovery] = useState(0);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookCounts, setWebhookCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState(null);
  const token = localStorage.getItem('masterToken') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [rec, wh] = await Promise.all([
        axios.get(`${API}/founding-vendors/master/recover`, { headers }),
        axios.get(`${API}/founding-vendors/master/webhook-events?limit=50`, { headers }).catch(() => ({ data: { events: [], counts: {} } })),
      ]);
      setPurchases(rec.data?.purchases || []);
      setNeedsRecovery(rec.data?.needs_recovery || 0);
      setWebhookEvents(wh.data?.events || []);
      setWebhookCounts(wh.data?.counts || {});
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const recoverOne = async (p) => {
    setRowBusy(p.session_id || p.vendor_id);
    try {
      const r = await axios.post(`${API}/founding-vendors/master/grant/${p.vendor_id}`, {}, { headers });
      const num = r.data?.founding_number;
      toast.success(num ? `Founding Vendor #${num} granted` : 'Granted');
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setRowBusy(null); }
  };

  const recoverAll = async () => {
    if (!window.confirm(`Promote ${needsRecovery} paid customer(s) who never got their Founding Vendor status?`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API}/founding-vendors/master/recover-all`, {}, { headers });
      const count = r.data?.recovered_count || 0;
      toast.success(`${count} vendor(s) recovered`);
      if ((r.data?.errors || []).length) toast.error(`${r.data.errors.length} failed — see console`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBulkBusy(false); }
  };

  const recoveryRows = purchases.filter(p => p.needs_recovery);
  const okRows       = purchases.filter(p => !p.needs_recovery);

  return (
    <div className="space-y-8" data-testid="master-recovery-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/50">Operations</p>
          <h1 className="text-3xl font-bold text-white mt-1" style={{ fontFamily: 'Syne, ui-sans-serif, system-ui' }}>Payment Recovery</h1>
          <p className="text-sm text-[#9CA3AF] mt-2 max-w-2xl">Founding-vendor payments that completed in Stripe but were never promoted (silent webhook misses). Click "Recover All" to fix every one in a single batch.</p>
        </div>
        <button
          onClick={load}
          className="px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold text-white/70 hover:text-white border border-white/15 rounded-lg flex items-center gap-1.5"
          data-testid="recovery-refresh"
        >
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {/* Webhook health */}
      <div className="bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 rounded-2xl px-5 py-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Activity size={16} className="text-[#A78BFA]" />
            <p className="text-[12px] tracking-[0.18em] font-bold uppercase text-white">Stripe Webhook Health</p>
          </div>
          <div className="flex items-center gap-4 text-[12px]">
            <span className="text-white/60">Total: <b className="text-white">{webhookCounts.total ?? 0}</b></span>
            <span className="text-green-400">Processed: <b>{webhookCounts.processed ?? 0}</b></span>
            <span className="text-white/50">Ignored: <b>{webhookCounts.ignored ?? 0}</b></span>
            <span className={`${(webhookCounts.errors ?? 0) > 0 ? 'text-red-400' : 'text-white/50'}`}>Errors: <b>{webhookCounts.errors ?? 0}</b></span>
          </div>
        </div>
        {webhookEvents.length === 0 ? (
          <p className="mt-3 text-[12px] text-yellow-300/80 flex items-center gap-1.5">
            <AlertTriangle size={11} /> No webhook events recorded yet. If you've taken payments and don't see events here, Stripe may be hitting the wrong URL — configure
            <code className="px-1.5 py-0.5 bg-black/30 rounded font-mono text-[11px]">https://entreprenatives.com/api/webhook/stripe</code> in your Stripe dashboard.
          </p>
        ) : (
          <div className="mt-3 max-h-40 overflow-y-auto space-y-1 text-[11px] font-mono">
            {webhookEvents.slice(0, 8).map((e, i) => (
              <div key={i} className="flex items-center gap-3 text-white/70">
                <span className="text-white/40 shrink-0">{(e.received_at || '').slice(11, 19)}</span>
                <span className={`shrink-0 ${e.status === 'processed' ? 'text-green-400' : e.status === 'error' ? 'text-red-400' : 'text-white/50'}`}>{e.status}</span>
                <span className="shrink-0">{e.event_type}</span>
                <span className="text-white/40 truncate">{e.session_id || e.note}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Stats + recover-all */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-[#171A2C]/60 backdrop-blur-xl border border-red-500/30 rounded-2xl px-5 py-4">
          <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-red-300">Needs Recovery</p>
          <p className="text-3xl font-bold text-red-400 mt-2" style={{ fontFamily: 'Syne, sans-serif' }} data-testid="needs-recovery-count">{needsRecovery}</p>
          <p className="text-[11px] text-white/55 mt-2">Paid but not yet promoted</p>
        </div>
        <div className="bg-[#171A2C]/60 backdrop-blur-xl border border-white/5 rounded-2xl px-5 py-4">
          <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/50">Total Purchases</p>
          <p className="text-3xl font-bold text-white mt-2" style={{ fontFamily: 'Syne, sans-serif' }}>{purchases.length}</p>
        </div>
        <button
          onClick={recoverAll}
          disabled={bulkBusy || needsRecovery === 0}
          className="bg-gradient-to-br from-[#8B5CF6] to-[#6D28D9] hover:from-[#A78BFA] hover:to-[#8B5CF6] disabled:opacity-30 disabled:cursor-not-allowed border border-[#A78BFA]/40 rounded-2xl px-5 py-4 text-left transition-all group"
          data-testid="recover-all-btn"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/80">One-Click</p>
              <p className="text-xl font-bold text-white mt-1" style={{ fontFamily: 'Syne, sans-serif' }}>{bulkBusy ? 'Recovering…' : 'Recover All'}</p>
              <p className="text-[11px] text-white/70 mt-1.5">Promote every paid-but-not-granted purchase.</p>
            </div>
            <Wand2 size={28} className="text-white opacity-70 group-hover:opacity-100" />
          </div>
        </button>
      </div>

      {loading ? (
        <div className="py-12 text-center"><Loader2 className="animate-spin inline text-[#A78BFA]" /></div>
      ) : (
        <>
          {/* Recovery needed */}
          {recoveryRows.length > 0 && (
            <div>
              <h2 className="text-[12px] tracking-[0.18em] font-bold uppercase text-red-300 mb-3 flex items-center gap-2">
                <AlertTriangle size={13} /> Needs Manual Recovery ({recoveryRows.length})
              </h2>
              <div className="space-y-2">
                {recoveryRows.map(p => (
                  <PurchaseRow key={p.session_id || p.vendor_id} p={p} busy={rowBusy} onRecover={recoverOne} />
                ))}
              </div>
            </div>
          )}
          {/* Already OK */}
          {okRows.length > 0 && (
            <div>
              <h2 className="text-[12px] tracking-[0.18em] font-bold uppercase text-green-300 mb-3 flex items-center gap-2">
                <CheckCircle2 size={13} /> Already Promoted ({okRows.length})
              </h2>
              <div className="space-y-2">
                {okRows.map(p => (
                  <PurchaseRow key={p.session_id || p.vendor_id} p={p} busy={rowBusy} onRecover={recoverOne} />
                ))}
              </div>
            </div>
          )}
          {purchases.length === 0 && (
            <div className="py-16 text-center text-white/60">
              <CheckCircle2 size={32} className="mx-auto text-green-400/60 mb-3" />
              No founding-vendor purchases recorded yet.
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PurchaseRow({ p, busy, onRecover }) {
  const created = p.created_at ? new Date(p.created_at).toLocaleString() : '—';
  return (
    <div className={`bg-[#171A2C]/60 backdrop-blur-xl border ${p.needs_recovery ? 'border-red-500/40' : 'border-white/5'} rounded-2xl px-5 py-4 flex items-center gap-5`} data-testid={`recover-row-${p.session_id || p.vendor_id}`}>
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600 flex items-center justify-center font-bold text-white shrink-0">
        <Award size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-bold text-white truncate">{p.business_name || p.vendor_id || 'Unknown vendor'}</p>
          {p.is_founding_vendor
            ? <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-green-400 bg-green-500/10 px-2 py-0.5 rounded">Founding #{p.founding_number}</span>
            : <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-red-400 bg-red-500/10 px-2 py-0.5 rounded">Not Promoted</span>}
          <span className="text-[10px] tracking-[0.18em] font-bold uppercase text-white/55 bg-white/5 px-2 py-0.5 rounded">${p.amount ?? '—'}</span>
        </div>
        <p className="text-xs text-white/65 mt-1">{p.owner_email || '—'} · session <span className="font-mono text-[11px]">{(p.session_id || '').slice(0, 24)}…</span> · {created}</p>
      </div>
      {p.needs_recovery && (
        <button
          onClick={() => onRecover(p)}
          disabled={busy === (p.session_id || p.vendor_id)}
          className="px-3 py-2 text-[10px] tracking-[0.18em] uppercase font-bold text-amber-300 hover:bg-amber-500/10 rounded transition-all flex items-center gap-1.5 disabled:opacity-40 border border-amber-300/30"
          data-testid={`recover-one-${p.session_id || p.vendor_id}`}
        >
          {busy === (p.session_id || p.vendor_id) ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />} Recover
        </button>
      )}
    </div>
  );
}
